import React, { useState, useEffect } from 'react';
import { StageProps } from '../WizardContainer';
import { profileService } from '../../../services/api';
import {
  DatingProfile,
  BODY_TYPE_OPTIONS,
  ETHNICITY_OPTIONS,
  RELIGION_OPTIONS,
  EDUCATION_OPTIONS,
} from '../../../types/profile';

interface Stage2Props extends StageProps {
  step: number;
}

const Stage2AboutYou: React.FC<Stage2Props> = ({
  onNext,
  onBack,
  isLoading,
  step,
}) => {
  const [profile, setProfile] = useState<Partial<DatingProfile>>({});

  useEffect(() => {
    const loadProfile = async () => {
      const existing = await profileService.getProfile();
      if (existing) {
        setProfile(existing);
      }
    };
    loadProfile();
  }, []);

  const updateField = (field: keyof DatingProfile, value: unknown) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = async () => {
    await profileService.updateProfile(profile);
    onNext(profile as Record<string, unknown>);
  };

  const renderStep1 = () => (
    <div className="wizard-step">
      <h3 className="wizard-step-title">Physical Attributes</h3>
      <p className="wizard-step-subtitle">Help others know what to expect</p>

      <div className="wizard-form-row">
        <div className="wizard-form-group">
          <label htmlFor="heightCm">Height (cm)</label>
          <input
            type="number"
            id="heightCm"
            value={profile.heightCm || ''}
            onChange={(e) => updateField('heightCm', parseInt(e.target.value) || null)}
            placeholder="175"
            min={100}
            max={250}
          />
        </div>
      </div>

      <div className="wizard-form-group">
        <label>Body Type</label>
        <div className="wizard-options-grid">
          {BODY_TYPE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`wizard-option-btn ${
                profile.bodyType === option.value ? 'selected' : ''
              }`}
              onClick={() => updateField('bodyType', option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="wizard-step">
      <h3 className="wizard-step-title">Background</h3>

      <div className="wizard-form-group">
        <label>Ethnicity</label>
        <div className="wizard-options-grid">
          {ETHNICITY_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`wizard-option-btn ${
                profile.ethnicity === option.value ? 'selected' : ''
              }`}
              onClick={() => updateField('ethnicity', option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="wizard-form-group">
        <label>Religion</label>
        <div className="wizard-options-grid">
          {RELIGION_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`wizard-option-btn ${
                profile.religion === option.value ? 'selected' : ''
              }`}
              onClick={() => updateField('religion', option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="wizard-form-group">
        <label>Education</label>
        <div className="wizard-options-grid">
          {EDUCATION_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`wizard-option-btn ${
                profile.education === option.value ? 'selected' : ''
              }`}
              onClick={() => updateField('education', option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="wizard-step">
      <h3 className="wizard-step-title">Tell us about yourself</h3>

      <div className="wizard-form-group">
        <label htmlFor="occupation">Occupation</label>
        <input
          type="text"
          id="occupation"
          value={profile.occupation || ''}
          onChange={(e) => updateField('occupation', e.target.value)}
          placeholder="What do you do?"
          maxLength={100}
        />
      </div>

      <div className="wizard-form-group">
        <label htmlFor="bio">Bio</label>
        <textarea
          id="bio"
          value={profile.bio || ''}
          onChange={(e) => updateField('bio', e.target.value)}
          placeholder="Share a bit about yourself, your passions, and what makes you unique..."
          rows={5}
          maxLength={2000}
        />
        <span className="wizard-hint">
          {(profile.bio?.length || 0)}/2000 characters
        </span>
      </div>

      <div className="wizard-form-group">
        <label htmlFor="headline">Headline</label>
        <input
          type="text"
          id="headline"
          value={profile.headline || ''}
          onChange={(e) => updateField('headline', e.target.value)}
          placeholder="A catchy one-liner about you"
          maxLength={150}
        />
        <span className="wizard-hint">This appears at the top of your profile</span>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (step) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return renderStep1();
    }
  };

  return (
    <div className="wizard-stage">
      {renderCurrentStep()}

      <div className="wizard-stage-actions">
        <button
          type="button"
          className="wizard-btn wizard-btn-secondary"
          onClick={onBack}
          disabled={isLoading}
        >
          Back
        </button>
        <button
          type="button"
          className="wizard-btn wizard-btn-primary"
          onClick={handleNext}
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : step === 3 ? 'Complete Stage' : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default Stage2AboutYou;
